// src/store/slices/tenant/userGroupSlice.ts
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import apiClient from '@/services/api-client';
import { ApiResponse, UserGroupItem } from '@/services/types';
import { API_ENDPOINTS } from '@/utils/constants/end-points';

export interface Permission {
  permissionId: number;
  name: string;
  code: string;
}

export interface SystemModule {
  moduleId: number;
  moduleName: string;
  children?: SystemModule[];
  permissions?: Permission[];
}

export const fetchUserGroups = createAsyncThunk<
  UserGroupItem[],
  void,
  { rejectValue: string }
>('userGroup/fetchAll', async (_, { rejectWithValue }) => {
  try {
    const res = await apiClient.get<ApiResponse<UserGroupItem[]>>(
      `${API_ENDPOINTS.MASTER}/user-groups`
    );

    // 👇 API now returns array directly
    return res.data.data;
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Failed to fetch user groups');
  }
});

export const fetchSystemModules = createAsyncThunk<
  SystemModule[],
  void,
  { rejectValue: string }
>('userGroup/fetchSystemModules', async (_, { rejectWithValue }) => {
  try {
    const res = await apiClient.get<ApiResponse<SystemModule[]>>(
      `${API_ENDPOINTS.MASTER}/system-modules/permissions`
    );
    return res.data.data;
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Failed to fetch system modules');
  }
});
interface UserGroupState {
  data: UserGroupItem[];
  systemModules: SystemModule[];
  loading: boolean;
  error: string | null;
}

const initialState: UserGroupState = {
  data: [],
  systemModules: [],
  loading: false,
  error: null,
};

const userGroupSlice = createSlice({
  name: 'userGroup',
  initialState,
  reducers: {
    clearUserGroups(state) {
      state.data = [];
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchUserGroups.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchUserGroups.fulfilled,
        (state, action: PayloadAction<UserGroupItem[]>) => {
          state.loading = false;
          state.data = action.payload;
        }
      )
      .addCase(fetchUserGroups.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Unknown error';
      })
      .addCase(fetchSystemModules.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchSystemModules.fulfilled,
        (state, action: PayloadAction<SystemModule[]>) => {
          state.loading = false;
          state.systemModules = action.payload;
        }
      )
      .addCase(fetchSystemModules.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Unknown error';
      });
  },
});

export const { clearUserGroups } = userGroupSlice.actions;
export default userGroupSlice.reducer;
